'use client';

/**
 * Shown when `/m/<business>` doesn't resolve.
 *
 * This is a guest who scanned a QR code or followed a link and landed nowhere ,
 * the least patient moment on the whole surface , so it has to recover fast and
 * never stall. Three fixes over the previous version:
 *
 *   · the lookups had no `catch`. A failing server action left `loading` true
 *     forever and the page sat on a skeleton with no message and no way out;
 *   · the search box appeared only after "Browse menus" was pressed, and had no
 *     label. It's now available with the results and properly labelled;
 *   · the result cards were three stacked blocks of centred text. They're now
 *     scannable rows with the business identity leading.
 *
 * The heading stays type-neutral: the slug didn't resolve, so there is no
 * business type to speak in , "Menu not found" is true for every type,
 * "Restaurant" was not (doc 46 §1).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { Compass, Loader2, MapPin, Search, SearchX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { getMenusBySimilarSlug, getAllMenusWithBusiness } from '@/lib/actions';
import { legacyMenuPath } from '@/lib/routing/place-href';

type MenuSuggestion = {
  id: string;
  slug: string;
  title?: string | null;
  business?: { name?: string | null; description?: string | null; logo_url?: string | null } | null;
};

export default function NotFoundMenuPage() {
  const params = useParams();
  const slug = typeof params.business === 'string' ? params.business : Array.isArray(params.business) ? params.business[0] : '';

  const [similarMenus, setSimilarMenus] = useState<MenuSuggestion[]>([]);
  const [allMenus, setAllMenus] = useState<MenuSuggestion[]>([]);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [browsing, setBrowsing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSimilar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const found = await getMenusBySimilarSlug(slug);
      setSimilarMenus((found as MenuSuggestion[]) || []);
    } catch {
      // Without this the page hung on a skeleton indefinitely.
      setError('We couldn’t reach the menu directory just now.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void loadSimilar();
  }, [loadSimilar]);

  const handleShowAll = useCallback(async () => {
    setBrowsing(true);
    setError(null);
    try {
      const all = await getAllMenusWithBusiness();
      setAllMenus((all as MenuSuggestion[]) || []);
      setShowAll(true);
    } catch {
      setError('We couldn’t load the menu directory. Try again in a moment.');
    } finally {
      setBrowsing(false);
    }
  }, []);

  const filteredMenus = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return allMenus;
    return allMenus.filter(
      (menu) =>
        menu.title?.toLowerCase().includes(needle) || menu.business?.name?.toLowerCase().includes(needle),
    );
  }, [allMenus, search]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="flex flex-col items-center text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <SearchX className="size-7" aria-hidden="true" />
          </span>
          <h1 className="mt-5 text-2xl font-semibold tracking-tight sm:text-3xl">Menu not found</h1>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
            There’s no menu at <span className="font-mono font-medium text-foreground">/m/{slug}</span>.
            {similarMenus.length > 0
              ? ' These look close , did you mean one of them?'
              : ' Check the link for a typo, or find the place below.'}
          </p>
        </div>

        {error ? (
          <div
            role="alert"
            className="mx-auto mt-8 flex max-w-lg flex-col items-center gap-3 rounded-[var(--radius-lg)] border border-border bg-card px-5 py-6 text-center"
          >
            <p className="text-sm leading-relaxed text-muted-foreground">{error}</p>
            <Button variant="outline" className="min-h-11" onClick={() => void loadSimilar()}>
              Try again
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-28 rounded-[var(--radius-lg)]" />
            ))}
          </div>
        ) : null}

        {!loading && similarMenus.length > 0 ? (
          <section className="mt-10">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Close matches
            </h2>
            <MenuGrid menus={similarMenus} />
          </section>
        ) : null}

        {!loading && !showAll ? (
          <div className="mt-10 flex flex-col items-center gap-3">
            <Button size="lg" className="min-h-12" disabled={browsing} onClick={() => void handleShowAll()}>
              {browsing ? <Loader2 className="size-4 animate-spin" /> : <Compass className="size-4" />}
              {browsing ? 'Loading menus…' : 'Browse all menus'}
            </Button>
            <p className="text-xs text-muted-foreground">Or ask the venue for a fresh link.</p>
          </div>
        ) : null}

        {showAll ? (
          <section className="mt-10">
            <div className="mx-auto max-w-md space-y-1.5">
              <Label htmlFor="menu-search" className="text-sm">
                Find a place
              </Label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  id="menu-search"
                  type="search"
                  placeholder="Search menus or businesses…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="h-11 pl-9 text-base"
                />
              </div>
            </div>

            <div className="mt-6">
              {filteredMenus.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                  {search.trim()
                    ? `Nothing matches “${search.trim()}”.`
                    : 'No menus are listed publicly right now.'}
                </p>
              ) : (
                <MenuGrid menus={filteredMenus} />
              )}
            </div>
          </section>
        ) : null}

        <p className="mt-14 text-center text-xs text-muted-foreground">
          Still lost?{' '}
          <Link href="/" className="font-medium text-foreground underline underline-offset-2">
            Go to Menuraq home
          </Link>
        </p>
      </div>
    </div>
  );
}

function MenuGrid({ menus }: { menus: MenuSuggestion[] }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {menus.map((menu) => (
        <li key={menu.id}>
          <Link
            href={legacyMenuPath(menu.slug)}
            className="group flex h-full items-start gap-3 rounded-[var(--radius-lg)] border border-border bg-card p-4 transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-[var(--shadow-md)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {menu.business?.logo_url ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={menu.business.logo_url}
                alt=""
                className="size-11 shrink-0 rounded-xl border border-border object-cover"
              />
            ) : (
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                <MapPin className="size-5" aria-hidden="true" />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-base font-semibold leading-snug">
                {menu.business?.name || menu.title || 'Menu'}
              </span>
              {menu.title && menu.business?.name ? (
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">{menu.title}</span>
              ) : null}
              {menu.business?.description ? (
                <span className="mt-1 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
                  {menu.business.description}
                </span>
              ) : null}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
